import { Box, xcss } from '@atlaskit/primitives';
import { AsyncSelect, type FormatOptionLabelMeta } from '@databases/atlaskit/Select';
import { authenticateJira } from '@databases/connections/jira-auth';
import JiraIssueIcon from '@databases/content-link/JiraIssueIcon';
import JiraIssueLabel from '@databases/content-link/JiraIssueLabel';
import JiraIssueStatus from '@databases/content-link/JiraIssueStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import {
  type CloudId,
  type JiraIssueRef,
  type JiraServerId,
} from '@databases/types/confluence';
import { DatabaseFieldType } from '@databases/types/database';
import { JiraAuthenticationError } from '@databases/utils/confluence/jira';
import {
  type ValueChange,
  type MultiChange,
  MultiValueChangeType,
} from '@databases/utils/database/changesets';
import { searchJiraIssues } from '@databases/utils/redux/jira/actions';
import { generateId } from '@databases/utils/utils';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import {
  CustomValueContainer,
  baseSelectStyleConfig,
  isRenderedInSidePanelStyling,
} from '../popover-edit-common';

import { type ResolvedValue, type Value } from './types';

const getOptionValue = (value: JiraIssueRef) => value.key;

const getOptionLabel = (value: JiraIssueRef) => value.key;

const optionContainerStyles = xcss({
  display: 'flex',
  gap: 'space.050',
  alignItems: 'center',
});

export interface JiraIssueSelectProps {
  cloudId: CloudId;
  serverId: JiraServerId;
  onChange: (value: Value[], change: ValueChange | null) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  resolvedValues: ResolvedValue[];
  isRenderedInSidePanel?: boolean;
  isMulti: boolean;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
}

const JiraIssueSelect: FunctionComponent<JiraIssueSelectProps> = ({
  cloudId,
  serverId,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  resolvedValues,
  isRenderedInSidePanel,
  isMulti,
  addDataSectionRef,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();

  const addJiraItemToCollab = useCallback(
    (val: Value) => {
      const jiraValues = [...resolvedValues, val];

      const delta: MultiChange<DatabaseFieldType.JIRA_ISSUE> = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: val,
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [val],
          };

      onChange(jiraValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta,
      });
    },
    [isMulti, onChange, resolvedValues]
  );

  const handleChange = useCallback(
    (val: JiraIssueRef | null) => {
      if (!val) {
        return;
      }
      const valToAdd = {
        _id: generateId(),
        key: 'key' in val ? val.key : '',
      };
      addJiraItemToCollab(valToAdd);
    },
    [addJiraItemToCollab]
  );

  const loadOptions = useCallback(
    async (inputValue: string) => {
      try {
        if (inputValue.length > 1) {
          return await dispatch(searchJiraIssues(cloudId, serverId, inputValue));
        }
        return [];
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (e) {
        if (e instanceof JiraAuthenticationError && e.authenticationPath) {
          const authPath = e.authenticationPath;

          let flagShown = false;

          if (flagShown) {
            return [];
          }

          currentSnackbarState.setMessage(
            formatMessage(messages['snackbar-error.jira.auth-required']),
            {
              id: 'jira-auth',
              type: 'error',
              showCloseButton: true,
              actions: [
                {
                  content: formatMessage(messages['snackbar-error.jira.authenticate']),
                  onClick: async () => {
                    await authenticateJira(
                      cloudId,
                      serverId,
                      authPath,
                      store,
                      formatMessage(messages['snackbar-error.enable-popup'])
                    );
                    flagShown = false;
                  },
                },
              ],
              onDismiss: () => {
                flagShown = false;
              },
            }
          );
          flagShown = true;

          return [];
        } else {
          throw e;
        }
      }
    },
    [cloudId, dispatch, formatMessage, serverId, store]
  );

  const formatOptionLabel = useCallback(
    (value: JiraIssueRef, meta: FormatOptionLabelMeta<JiraIssueRef>) => {
      if (meta.context === 'menu') {
        return (
          <Box xcss={optionContainerStyles} testId="jira-issue-option-container">
            <JiraIssueIcon issue={value} />
            <JiraIssueLabel issue={value} />
            <JiraIssueStatus issue={value} />
          </Box>
        );
      }
    },
    []
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      if (!isRenderedInSidePanel) {
        addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
      }
    },
    [onBlur, addDataSectionRef, isRenderedInSidePanel]
  );

  return (
    <AsyncSelect<JiraIssueRef>
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      loadOptions={loadOptions}
      getOptionValue={getOptionValue}
      onFocus={onFocus}
      onBlur={handleBlur}
      blurInputOnSelect={false}
      closeMenuOnSelect={false}
      formatOptionLabel={formatOptionLabel}
      placeholder={formatMessage(
        isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
          ? messages['jira-issue-picker-popover.search-text-issue-terminology-refresh-m4']
          : messages['jira-issue-picker-popover.search-text']
      )}
      loadingMessage={() =>
        formatMessage(messages['field-settings.select-picker-loading-text'])
      }
      noOptionsMessage={() => formatMessage(messages['generic-select.no-options'])}
      autoFocus={autoFocus}
      styles={
        isRenderedInSidePanel ? isRenderedInSidePanelStyling : baseSelectStyleConfig
      }
      controlShouldRenderValue={false}
      {...(!isRenderedInSidePanel && {
        menuPortalTarget: null,
        defaultMenuIsOpen: true,
        menuIsOpen: true,
        components: {
          IndicatorsContainer: () => null,
          ValueContainer: CustomValueContainer,
        },
      })}
    />
  );
};

export default JiraIssueSelect;
